export interface IAttribution {
  utm: string;
  originationUrl: string;
}

// utm_* params pass through unchanged.
const UTM_PASSTHROUGH = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

// Click IDs are renamed to their payload keys.
const RENAMED: Record<string, string> = {
  gclid: "google_click_id",
  gad_source: "google_ads_source",
  gad_campaignid: "google_campaign_id",
  fbclid: "meta_click_id",
};

const decodeParam = (
  params: URLSearchParams,
  key: string,
  fallback: string,
): string => {
  const raw = params.get(key);
  return raw ? decodeURIComponent(raw) : fallback;
};

const landingPageFrom = (href: string): string => {
  try {
    const url = new URL(href);
    return `${url.origin}${url.pathname}`;
  } catch {
    return "";
  }
};

/**
 * Paid-acquisition attribution passthrough. The loader forwards the HOST page's
 * query string plus `__origin` (host href) and `__referrer` into the iframe, so
 * everything is read from this document's own query — but the marketing params
 * are renamed and `originationUrl` / `landing_page` / `referrer` reflect the
 * HOST page, never the iframe URL. Absent params are omitted; no PII is ever
 * read from or written to a URL.
 */
export const buildAttribution = (): IAttribution => {
  const source = new URLSearchParams(window.location.search);
  const utm = new URLSearchParams();

  UTM_PASSTHROUGH.forEach((key) => {
    const value = source.get(key);
    if (value) utm.set(key, value);
  });

  Object.entries(RENAMED).forEach(([from, to]) => {
    const value = source.get(from);
    if (value) utm.set(to, value);
  });

  // gbraid wins over wbraid when both are present.
  const braid = source.get("gbraid") || source.get("wbraid");
  if (braid) utm.set("google_braid", braid);

  const originationUrl = decodeParam(source, "__origin", window.location.href);
  const referrer = decodeParam(source, "__referrer", document.referrer);

  // Derived from the host page, query stripped (no PII).
  const landing = landingPageFrom(originationUrl);
  if (landing) utm.set("landing_page", landing);

  if (referrer) utm.set("referrer", referrer);

  return { utm: utm.toString(), originationUrl };
};
